import cn from "classnames"
import { ResizeSensor } from "css-element-queries"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { GlobalHotKeys } from "react-hotkeys"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import MediaQuery from "@/common/components/media_query"
import env from "@/common/env"
import tailwindTheme from "@/common/styles/theme.module.css"
import { isTablet } from "@/common/util"
import { ReactComponent as BorderIcon } from "@/images/signomatic/border.svg"
import { ReactComponent as FixmethodIcon } from "@/images/signomatic/fixmethod.svg"
import FixmethodIcons from "@/images/signomatic/fixmethods"
import { ReactComponent as ImageIcon } from "@/images/signomatic/image.svg"
import { ReactComponent as MaterialIcon } from "@/images/signomatic/material.svg"
import { ReactComponent as PresetsBadgesIcon } from "@/images/signomatic/presets_badges.svg"
import { ReactComponent as PresetsDoorIcon } from "@/images/signomatic/presets_door.svg"
import { ReactComponent as PresetsMaterialIcon } from "@/images/signomatic/presets_material.svg"
import { ReactComponent as PresetsTagsIcon } from "@/images/signomatic/presets_tags.svg"
import { ReactComponent as ProductsIcon } from "@/images/signomatic/products.svg"
import { ReactComponent as ShapeIcon } from "@/images/signomatic/shape.svg"
import ShapeIcons from "@/images/signomatic/shapes"
import SigntypeIcons from "@/images/signomatic/signtypes"
import { ReactComponent as SizeIcon } from "@/images/signomatic/size.svg"
import { ReactComponent as TextIcon } from "@/images/signomatic/text.svg"
import Sign from "@/signomatic/app/sign"
import { allPresets, matchPreset } from "@/signomatic/app/sign_validator"
import AdminToolbox from "@/signomatic/components/admin_toolbox"
import BorderToolbox from "@/signomatic/components/border_toolbox"
import ColorToolbox from "@/signomatic/components/color_toolbox"
import FixmethodToolbox from "@/signomatic/components/fixmethod_toolbox"
import ImageToolbox from "@/signomatic/components/image_toolbox"
import MaterialToolbox from "@/signomatic/components/material_toolbox"
import PresetToolbox from "@/signomatic/components/preset_toolbox"
import ProductToolbox from "@/signomatic/components/product_toolbox"
import ProductionTypeToolbox from "@/signomatic/components/production_type_toolbox"
import ShapeToolbox from "@/signomatic/components/shape_toolbox"
import SidesToolbox from "@/signomatic/components/sides_toolbox"
import SizeToolbox from "@/signomatic/components/size_toolbox"
import SubmenuToolbox from "@/signomatic/components/submenu_toolbox"
import TextToolbox from "@/signomatic/components/text_toolbox"
import ToolboxButton, { type ToolboxButtonProps } from "@/signomatic/components/toolbox_button"
import TypeToolbox from "@/signomatic/components/type_toolbox"
import { ActiveButtonContext } from "@/signomatic/contexts"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { HandleSides, More, ArrowDownLarge, ChevronRight } from "@/skylticons"

export interface ToolboxStripItemProps {
  sign: Sign
  desktop?: boolean
  tablet?: boolean
  presetGroupName?: string | null
}

const Toolboxes: StringMap<React.ComponentType<ToolboxStripItemProps>> = {
  products: ProductToolbox,
  presets: PresetToolbox,
  type: TypeToolbox,
  production_type: ProductionTypeToolbox,
  size: SizeToolbox,
  shape: ShapeToolbox,
  fixmethod: FixmethodToolbox,
  color: ColorToolbox,
  material: MaterialToolbox,
  border: BorderToolbox,
  text: TextToolbox,
  image: ImageToolbox,
  sides: SidesToolbox,
  submenu: SubmenuToolbox,
  admin: AdminToolbox,
}

type ScrollDirection = "top" | "bottom"

interface ScrollButtonProps {
  readonly direction: ScrollDirection
  readonly func: (e: React.MouseEvent, direction: ScrollDirection) => void
}

const ScrollButton = ({ direction, func }: ScrollButtonProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("scroll_button z-20 h-10 py-0.5", direction)} onClick={e => func(e, direction)}>
      <ArrowDownLarge />
      {env.locale !== "es-ES" && <span className="paragraph-2xs bold light mb-0">{t("misc.scroll")}</span>}
    </div>
  )
}

interface ToolboxStripOwnProps {
  sign: Sign
}

interface ToolboxStripStateProps {
  signType: string
  toolbox: string | null
  signState: SignState | null
  data: SignData | null
  activeObject: SignObjectState | null
  metaProduct: string
}

interface ToolboxStripDispatchProps {
  setToolbox: typeof actions.setToolbox
  resetToolbox: typeof actions.resetToolbox
}

type ToolboxStripProps = ToolboxStripOwnProps & ToolboxStripStateProps & ToolboxStripDispatchProps

const ToolboxStrip: React.FC<ToolboxStripProps> = ({
  sign,
  signType,
  toolbox,
  signState,
  data,
  activeObject,
  metaProduct,
  setToolbox,
  resetToolbox,
}) => {
  const [whoo, setWhoo] = useState<boolean>()
  const [scrollable, setScrollable] = useState(false)
  const [scrolledTop, setScrolledTop] = useState(true)
  const [scrolledBottom, setScrolledBottom] = useState(true)
  const [scrollLeft, setScrollLeft] = useState(0)
  const scrollBox = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<StringMap<React.RefObject<HTMLDivElement>>>({})

  const getButtonRef = (name?: string | null): React.RefObject<HTMLDivElement> | null => {
    if (!name) {
      return null
    }

    if (!buttonRefs.current[name]) {
      buttonRefs.current[name] = React.createRef<HTMLDivElement>()
    }

    return buttonRefs.current[name]
  }

  const getButton = useCallback((name: string): HTMLDivElement | null => {
    const ref = getButtonRef(name)

    if (ref && ref.current) {
      return ref.current
    }

    return null
  }, [])

  const openProductsOnRoot = useCallback(() => {
    if (signType === "") {
      const button = getButton("products")

      if (button) {
        button.click()
      }
    }
  }, [getButton, signType])

  const openToolboxDblClick = useCallback(() => {
    if (activeObject) {
      if (Sign.isImage(activeObject)) {
        setToolbox("image")
      } else if (Sign.isText(activeObject)) {
        if (!isTablet()) {
          return
        }

        setToolbox("text")
      }
    }
  }, [activeObject, setToolbox])

  const updateOffsets = () => {
    const box = scrollBox.current

    if (box) {
      const horizontal = box.clientHeight > box.clientWidth

      const scrollable = horizontal ? box.scrollHeight > box.clientHeight : box.scrollWidth > box.offsetWidth

      const scrolledTop = horizontal ? box.scrollTop <= 0 : box.scrollLeft <= 0

      const scrolledBottom = horizontal
        ? box.scrollTop >= box.scrollHeight - box.offsetHeight
        : box.scrollLeft >= box.scrollWidth - box.offsetWidth

      setScrollLeft(box.scrollLeft)
      setScrollable(scrollable)
      setScrolledTop(scrolledTop)
      setScrolledBottom(scrolledBottom)
    }
  }

  useEffect(() => {
    window.addEventListener("resize", updateOffsets)
    let sensor: ResizeSensor | null = null

    if (scrollBox.current) {
      sensor = new ResizeSensor(scrollBox.current, updateOffsets)
    }

    sign.onDoubleClick.subscribe(openToolboxDblClick)
    updateOffsets()

    const timeout = setTimeout(() => {
      openProductsOnRoot()
    }, 0)

    return () => {
      window.removeEventListener("resize", updateOffsets)

      if (sensor) {
        sensor.detach(updateOffsets)
      }

      sign.onDoubleClick.unsubscribe(openToolboxDblClick)
      clearTimeout(timeout)
    }
  }, [openProductsOnRoot, openToolboxDblClick, sign.onDoubleClick])

  useEffect(() => {
    updateOffsets()
  }, [toolbox])

  const whooItUp = () => {
    setWhoo(true)
    setTimeout(() => setWhoo(false), 5000)
  }

  const handleScroll = (e: React.MouseEvent, direction: ScrollDirection) => {
    e.preventDefault()
    e.stopPropagation()

    const box = scrollBox.current

    if (!box) {
      return
    }

    const horizontal = box.clientHeight > box.clientWidth
    const value = 105

    if (direction === "top") {
      if (horizontal ? box.scrollTop === 0 : box.scrollLeft === 0) {
        return
      }

      if (horizontal) {
        box.scrollTop -= value
      } else {
        box.scrollLeft -= value
      }
    } else {
      if (
        horizontal
          ? box.scrollTop === box.scrollHeight - box.offsetHeight
          : box.scrollLeft === box.scrollWidth - box.offsetWidth
      ) {
        return
      }

      if (horizontal) {
        box.scrollTop += value
      } else {
        box.scrollLeft += value
      }
    }
  }

  const buttonList = (): Array<Partial<ToolboxButtonProps>> => {
    const hasPresets = data && allPresets(data).length > 0
    const imagesAllowed = data?.images
    const textsAllowed = data?.texts

    const showMaterial =
      !hasPresets && data && data.materials.filter(material => !material.filters || !material.filters.hidden).length > 1

    const buttons: Array<Partial<ToolboxButtonProps>> = [{ toolbox: "products" }]

    if (metaProduct) {
      if (["meta_plastic_sign", "meta_name_sign"].includes(metaProduct)) {
        buttons.push({ toolbox: "production_type" })
      } else {
        buttons.push({ toolbox: "type" })
      }
    }

    if (hasPresets) {
      for (const key in data.preset_groups) {
        if (Object.prototype.hasOwnProperty.call(data.preset_groups, key)) {
          const group = data.preset_groups[key]

          if (!group?.hidden || (signState && group?.presets?.some(p => matchPreset(signState, p)))) {
            buttons.push({ toolbox: `presets_${key}`, labelKey: `toolbox.${key}` })
          }
        }
      }
    }

    if (["vinyl_text", "contour_cut"].includes(signType) && textsAllowed) {
      buttons.push({ toolbox: "text" })
    }

    if (sign.customSizeAllowed) {
      buttons.push({ toolbox: "size" })
    }

    if (showMaterial && signType === "rollup") {
      buttons.push({ toolbox: "material" })
    }

    const shouldAddShape = () => {
      if (signType === "thermal_transfer_label") {
        return false
      }
      if (!hasPresets) {
        return true
      }
      if (["double_sided_sign", "wood_sign"].includes(signType)) {
        return true
      }
      if (
        signType === "label_plates" &&
        signState &&
        ["regular", "aluminium", "aluminium_lacquered", "stainless"].includes(signState.material)
      ) {
        return true
      }
      return false
    }

    if (shouldAddShape()) {
      if (data && data.shapes.length > 1) {
        buttons.push({ toolbox: "shape" })
      } else if (signType === "poster" && data?.shapes) {
        buttons.push({ toolbox: "shape" })
      }
    }

    buttons.push({ toolbox: "fixmethod" })

    if (sign.hasColorOptions) {
      buttons.push({ toolbox: "color" })
    }

    if (showMaterial && signType !== "rollup") {
      buttons.push({ toolbox: "material" })
    }

    if (
      ["double_sided_sign", "wood_sign"].includes(signType) ||
      (signType === "label_plates" && signState && ["regular", "aluminium_lacquered"].includes(signState.material)) ||
      (!hasPresets &&
        ![
          "contour_cut",
          "tactile_sign",
          "etched_aluminium_sign",
          "stainless_steel_sign",
          "thermal_transfer_label",
        ].includes(signType))
    ) {
      buttons.push({ toolbox: "border" })
    }

    if (!["vinyl_text", "contour_cut"].includes(signType) && textsAllowed) {
      buttons.push({ toolbox: "text" })
    }

    if (imagesAllowed) {
      buttons.push({ toolbox: "image" })
    }

    if (signType === "double_sided_sign" && isTablet()) {
      buttons.push({ toolbox: "sides" })
    }

    if (isTablet()) {
      buttons.push({ toolbox: "submenu" })
    }

    if (ClientData.admin) {
      buttons.push({ toolbox: "admin" })
    }

    return buttons
  }

  const buttonIcon = (toolbox?: string) => {
    const imageType = metaProduct ? metaProduct : signType

    switch (toolbox) {
      case "products":
        return signType.length > 0 ? SigntypeIcons[imageType] : ProductsIcon
      case "size":
        return SizeIcon
      case "material":
      case "presets":
      case "type":
      case "production_type":
        return MaterialIcon
      case "border":
        return BorderIcon
      case "text":
        return TextIcon
      case "image":
        return ImageIcon
      case "sides":
        return HandleSides
      case "submenu":
        return More
      case "shape":
        if (signState && signState.shape === "contour") {
          switch (signState.contour) {
            case "l":
              return ShapeIcons.contour_large
            case "m":
              return ShapeIcons.contour_medium
            case "s":
              return ShapeIcons.contour_small
          }
        }

        return signState && signState.shape && ShapeIcons[signState.shape] ? ShapeIcons[signState.shape] : ShapeIcon
      case "fixmethod":
        return signState &&
          signState.fixmethod &&
          !["none", null].includes(signState.fixmethod) &&
          FixmethodIcons[signState.fixmethod]
          ? FixmethodIcons[signState.fixmethod]
          : FixmethodIcon
      case "presets_badges":
        return PresetsBadgesIcon
      case "presets_door":
        return PresetsDoorIcon
      case "presets_material":
        return PresetsMaterialIcon
      case "presets_tags":
      case "presets_label_print":
      case "presets_label_laser":
        return PresetsTagsIcon
    }
  }

  const renderButton = (props: Partial<ToolboxButtonProps>) => {
    const disabled = props.toolbox !== "products" && signType === ""
    const active = props.active || props.toolbox === toolbox
    const signColor = signState && data ? (data.transparent ? signState.contentColor : signState.color) : ""

    const click = () => {
      if (active) {
        resetToolbox()
        sign.deleteEmptyObjects()
      } else {
        if (["image", "text"].includes(props.toolbox || "")) {
          sign.setObjectActive(null)
        }

        if (activeObject) {
          sign.exitInlineEditing()
        }

        setToolbox(props.toolbox || null)
      }
    }

    if (props.toolbox === "material" && signType !== "rollup" && data) {
      const materials = data.materials.filter(
        m => m.name !== "regular" && (!m.filters || (!m.filters.hidden && !m.filters.legacy))
      )

      if (materials[0] && materials.length === 1) {
        props.labelKey = `material_type.${materials[0].name}`
      }
    }

    if (signState?.metaProduct) {
      switch (props.toolbox) {
        case "presets_material":
          props.labelKey = "toolbox.wood_type"
          break
        case "presets_door":
          props.labelKey = "toolbox.badges"
          break
      }
    }

    const defaultProps: ToolboxButtonProps = {
      icon: buttonIcon(props.toolbox),
      buttonRef: getButtonRef(props.toolbox),
      active,
      onClick: click,
      whoo,
      disabled,
      signColor,
    }

    return <ToolboxButton key={props.toolbox} {...defaultProps} {...props} />
  }

  const renderToolbox = (current: string, desktop: boolean, tablet: boolean) => {
    let presetGroupName: string | null = null

    if (current && current.startsWith("presets")) {
      presetGroupName = current.replace("presets_", "")
      current = "presets"
    }

    const Box = Toolboxes[current]

    if (!Box || (!data && current !== "products")) {
      return null
    }

    return <Box key={current} sign={sign} desktop={desktop} tablet={tablet} presetGroupName={presetGroupName} />
  }

  const keyMap = { WHOO: "up up down down left right left right b a" }
  const handlers = { WHOO: whooItUp }

  const box = scrollBox.current
  const mobileScrollVisible = box && scrollLeft ? scrollLeft >= box.scrollWidth - box.offsetWidth - 48 : false

  return (
    <div
      className={cn("toolbox-strip w-screen lg:flex lg:w-auto", {
        "lg:mb-0": signType === "",
        "lg:mb-16": signType !== "",
      })}
    >
      <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
      <div className="menu_outer lg:w-[104px] xl:w-36">
        {scrollable && !scrolledTop && <ScrollButton direction="top" func={handleScroll} />}
        {scrollable && !scrolledBottom && <ScrollButton direction="bottom" func={handleScroll} />}
        <div className="menu_inner z-0 w-full p-0 lg:w-[104px] xl:w-36" ref={scrollBox} onScroll={updateOffsets}>
          {scrollable && !mobileScrollVisible && (
            <MediaQuery query={`(max-width: ${tailwindTheme.screensMdmax})`}>
              <div
                className="button button-secondary button-small icon-only raised scroll-right z-10 mb-0"
                onClick={e => handleScroll(e, "bottom")}
              >
                <ChevronRight />
              </div>
            </MediaQuery>
          )}
          {buttonList().map(props => renderButton(props))}
        </div>
      </div>
      <MediaQuery query={`(min-width: ${tailwindTheme.screensXl})`}>
        {(desktop: boolean) => (
          <MediaQuery query={`(min-width: ${tailwindTheme.screensMd}) and (max-width: ${tailwindTheme.screensXl})`}>
            {(tablet: boolean) => (
              <ActiveButtonContext.Provider value={getButtonRef(toolbox) || undefined}>
                {toolbox && renderToolbox(toolbox, desktop, tablet)}
              </ActiveButtonContext.Provider>
            )}
          </MediaQuery>
        )}
      </MediaQuery>
    </div>
  )
}

export default connect(
  ({ toolbox, currentSignData, activeObject, signType, signState, metaProduct }: State): ToolboxStripStateProps => ({
    toolbox,
    signType,
    signState,
    activeObject,
    data: currentSignData,
    metaProduct,
  }),
  {
    setToolbox: actions.setToolbox,
    resetToolbox: actions.resetToolbox,
  }
)(ToolboxStrip)
